import { Link } from '@tanstack/react-router';
import { formatDistanceToNowStrict } from 'date-fns';

import { Badge } from '@apps/web/components/ui/badge';
import {
    type OutreachOutlierReason,
    type OutreachOutlierRun,
} from '@libraries/typescript/api_client';

type OutlierFeedProps = {
    readonly outliers: Array<OutreachOutlierRun>;
    readonly emptyMessage: string;
};

const REASON_LABELS: Record<OutreachOutlierReason, string> = {
    zero_caregiver_suggestions: '0 suggestions',
    stale_idle: 'Stale / abandoned',
    cancelled_before_contact: 'Cancelled before contact',
    tool_failures: 'Tool failures',
    compaction: 'Compaction',
    run_failed: 'Run failed',
};

const DESTRUCTIVE_REASONS: ReadonlySet<OutreachOutlierReason> = new Set([
    'tool_failures',
    'run_failed',
    'zero_caregiver_suggestions',
]);

function reasonLabel(
    run: OutreachOutlierRun,
    reason: OutreachOutlierReason,
): string {
    if (reason === 'tool_failures' && run.failed_tool_call_count > 1) {
        return `${REASON_LABELS[reason]} ×${run.failed_tool_call_count}`;
    }
    if (reason === 'compaction' && run.compaction_count > 1) {
        return `${REASON_LABELS[reason]} ×${run.compaction_count}`;
    }
    return REASON_LABELS[reason];
}

function OutlierRow({ run }: { readonly run: OutreachOutlierRun }) {
    return (
        <div className="bg-card flex flex-col gap-1.5 rounded-md border p-2.5 text-sm">
            <div className="flex items-center justify-between gap-2">
                <span className="min-w-0 truncate font-medium">
                    {run.org_name}
                </span>
                <span className="text-muted-foreground shrink-0 text-xs tabular-nums">
                    {formatDistanceToNowStrict(new Date(run.created_at), {
                        addSuffix: true,
                    })}
                </span>
            </div>
            <div className="flex flex-wrap items-center gap-1">
                <Badge variant="outline" className="uppercase">
                    {run.outreach_status}
                </Badge>
                {run.reasons.map((reason) => (
                    <Badge
                        key={reason}
                        variant={
                            DESTRUCTIVE_REASONS.has(reason)
                                ? 'destructive'
                                : 'secondary'
                        }
                    >
                        {reasonLabel(run, reason)}
                    </Badge>
                ))}
            </div>
            <div className="flex items-center gap-3 text-xs">
                <Link
                    to="/outreaches/$callout-id"
                    params={{ 'callout-id': run.outreach_id }}
                    search={{
                        organizationId: run.organization_id,
                        mode: 'live',
                    }}
                    className="text-primary hover:underline"
                >
                    Outreach
                </Link>
                {run.agent_conversation_id ? (
                    <Link
                        to="/admin/agent-traces/$conversationId"
                        params={{ conversationId: run.agent_conversation_id }}
                        className="text-primary hover:underline"
                    >
                        Agent trace
                    </Link>
                ) : null}
            </div>
        </div>
    );
}

export function OutlierFeed({ outliers, emptyMessage }: OutlierFeedProps) {
    if (outliers.length === 0) {
        return (
            <p className="text-muted-foreground py-6 text-center text-xs">
                {emptyMessage}
            </p>
        );
    }
    return (
        <div className="flex flex-col gap-2">
            {outliers.map((run) => (
                <OutlierRow key={run.outreach_id} run={run} />
            ))}
        </div>
    );
}
